import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toInteger } from 'lodash';
import { ErrorBoundary } from 'react-error-boundary';
import { getDateString } from 'js/lib/DateUtils';
import {
  ErrorBanner,
  FlowRunLogsHeader,
  LogLine,
  LogList,
  LogListFooter,
  LogListHeader,
  LogDetailPopover,
  LogsNavigationPanel,
  SkeletonLogLine,
} from 'js/components/data_flows/cards/FlowRunLogs/components';
import { FlattenedLogItem, useFlattenedLogs } from './hooks/useFlattenedLogs';
import { useFlowRunsLogs } from './hooks/useFlowRunsLogs';
import { useLogPopover } from './hooks/useLogPopover';
import { useSortOrder } from './hooks/useSortOrder';
import { RunOption } from './components/RunSelector/RunSelector';
import ErrorFallback from 'js/components/elements/errors/ErrorFallback';
import { defineds } from 'js/components/elements/datepicker/defaultRanges';
import { useGetMetaInfoByConnectorType } from 'new/hooks/useGetMetaInfoByConnectorType';
import { useFeatureFlag } from 'new/hooks/useFeatureFlags';
import { buildLogsFileName } from './utils/buildLogsFileName';
import { calculateTotalLogsCount } from './utils/calculateTotalLogsCount';
import { DISPLAY, ERROR_MESSAGES, SORT_ORDER, TIMING } from './constants';
/**
 * Styles
 */
import s from './FlowRunLogs.module.scss';

/**
 * FlowRunLogs Component
 *
 * Displays execution logs for a specific flow run with filtering, pagination,
 * and real-time updates.
 *
 * @param originNodeId - The origin node ID used for fetching logs from the API
 * @param flowNodeId - The flow node ID displayed in the header
 * @param connectorType - The connector type used to fetch display metadata
 * @param runId - The run ID timestamp, or '0' for explore mode
 * @param onClose - Callback when the logs view is closed
 * @param exploreFlowLogs - Whether this is opened from "Explore Flow Logs" button
 */
type Props = {
  originNodeId: string;
  flowNodeId: number;
  connectorType: string;
  runId?: string | null;
  onClose: () => void;
  exploreFlowLogs?: boolean;
  createdAt?: string | null;
  config?: {
    skipClearLogsOnUnmount?: boolean;
    autoRefreshIntervalValue?: number;
    omitRunSelector?: boolean;
    omitLogScroller?: boolean;
    omitSortOrderSelector?: boolean;
    omitNavigationPanelControls?: boolean;
  };
};

const FlowRunLogs = ({
  originNodeId,
  flowNodeId,
  connectorType,
  runId = null,
  onClose,
  exploreFlowLogs = false,
  createdAt = null,
  config,
}: Props) => {
  const {
    omitRunSelector = false,
    omitSortOrderSelector = false,
    omitNavigationPanelControls = false,
    skipClearLogsOnUnmount = false,
    autoRefreshIntervalValue = TIMING.AUTO_REFRESH_INTERVAL,
  } = config ?? {};

  const metaInfo = useGetMetaInfoByConnectorType(connectorType);
  const isFlowInsightsV2Enabled = useFeatureFlag('UI_ENABLE_FLOW_INSIGHTS_V2');
  const previousLogCountRef = useRef<number>(0);
  const isLoadingMoreRef = useRef<boolean>(false);
  const { querySortOrder, viewSortOrder, setQuerySortOrder, handleSortOrderChange } =
    useSortOrder();
  const [selectedRunId, setSelectedRunId] = useState<string | null>(runId);

  const {
    paging,
    actions,
    hasError,
    isFetchingLogs,
    showInitialLoader,
    runs: { data: runsData },
    logs: { data: logs, meta },
    filter: { buildLevelFilterOptions, selectedSeverityLabel, filterBy },
    date: { startDate, dateRange, onStartDateChanged },
    repository: { getFlowRunLogsWithFacets },
  } = useFlowRunsLogs({
    originNodeId,
    createdAt,
    exploreFlowLogs,
    viewSortOrder,
    runId: selectedRunId,
    sortOrder: querySortOrder,
    autoRefreshIntervalValue,
    skipClearLogsOnUnmount,
  });

  const flowRunLogsWithFacets = useMemo(() => {
    return getFlowRunLogsWithFacets();
  }, [getFlowRunLogsWithFacets]);

  const totalLogsCount = useMemo(() => {
    return flowRunLogsWithFacets?.total;
  }, [flowRunLogsWithFacets?.total]);

  const flattenedLogs = useFlattenedLogs(
    logs,
    meta,
    totalLogsCount,
    isFlowInsightsV2Enabled,
    viewSortOrder,
  );
  const totalLogCount = useMemo(() => calculateTotalLogsCount(logs), [logs]);

  const {
    popoverLogObject,
    popoverAnchorEl,
    handleLogLineClick,
    handleLogLineKeyDown,
    handlePopoverClose,
    getNavigationHandlers,
  } = useLogPopover({ logItems: flattenedLogs });

  const filteredFlattenedLogs = useMemo(
    () => flattenedLogs.filter((item) => item.type !== 'empty'),
    [flattenedLogs],
  );

  const logsToCopy = useMemo(
    () => filteredFlattenedLogs.map((flattenedLogItem) => flattenedLogItem.log!),
    [filteredFlattenedLogs],
  );

  const isTimeWindowMode = exploreFlowLogs && !selectedRunId;
  const copyAllTooltip = isTimeWindowMode
    ? 'Copy all logs loaded for the selected time window'
    : 'Copy all logs loaded for this run';

  const logsWithSkeletons = useMemo(() => {
    if (showInitialLoader) {
      return Array.from({ length: DISPLAY.SKELETON_LINES_COUNT }, (_, i) => ({
        id: `skeleton-initial-${i}`,
        type: 'skeleton' as const,
        runId: selectedRunId,
        lineNumber: i + 1,
      }));
    }

    return isFetchingLogs ? filteredFlattenedLogs : flattenedLogs;
  }, [flattenedLogs, filteredFlattenedLogs, showInitialLoader, selectedRunId, isFetchingLogs]);

  useEffect(() => {
    if (previousLogCountRef.current > 0 && totalLogCount > previousLogCountRef.current) {
      previousLogCountRef.current = totalLogCount;
    }
  }, [totalLogCount]);

  const maxLineNumber = useMemo(() => {
    // @ts-ignore
    return logsWithSkeletons.reduce((max: number, item) => {
      return item.type === 'log' && item.lineNumber ? Math.max(max, item.lineNumber) : max;
    }, 0);
  }, [logsWithSkeletons]);

  const lineNumberElementWidth =
    (Math.max(String(maxLineNumber).length, DISPLAY.MIN_LINE_NUMBER_DIGITS) + 1) *
    DISPLAY.LINE_NUMBER_CHAR_WIDTH;

  const handleLoadMoreLogs = useCallback(async () => {
    if (isLoadingMoreRef.current) return;

    isLoadingMoreRef.current = true;
    previousLogCountRef.current = totalLogCount;

    await actions.loadMoreLogs?.();

    isLoadingMoreRef.current = false;
  }, [totalLogCount, actions]);

  const onChangeSelectedRunId = useCallback(
    (runIdTimestamp: string) => {
      setSelectedRunId(runIdTimestamp);
      const runStartDateValue = toInteger(runIdTimestamp);
      onStartDateChanged(
        {
          from: runStartDateValue,
          to: defineds.endOfToday.getTime(),
        },
        runIdTimestamp,
      );
    },
    [onStartDateChanged],
  );

  const clearSelectedRunOption = useCallback(() => {
    /**
     * Prevent unnecessary state updates and API calls
     */
    if (runId === selectedRunId) return;

    onChangeSelectedRunId(runId);
  }, [runId, selectedRunId, onChangeSelectedRunId]);

  const selectedRunOption = useMemo(() => {
    if (!selectedRunId) return null;

    return {
      value: selectedRunId,
      label: getDateString(toInteger(selectedRunId), 'MMM D, YYYY h:mma'),
      onClick: (option: RunOption) => onChangeSelectedRunId(option.value),
    };
  }, [selectedRunId, onChangeSelectedRunId]);

  const runsOptions = useMemo(() => {
    if (!runsData) return [];
    return Object.keys(runsData).map((runIdOption) => {
      const runDate = getDateString(toInteger(runIdOption), 'MMM D, YYYY h:mma');
      return {
        value: runIdOption,
        onClick: () => onChangeSelectedRunId(runIdOption),
        label: `${runDate}  (${runIdOption})`,
      };
    });
  }, [runsData, onChangeSelectedRunId]);

  const levelOptions = useMemo(() => {
    return buildLevelFilterOptions();
  }, [buildLevelFilterOptions]);

  const logScrollerLabel = useMemo(() => {
    if (selectedRunId) {
      return querySortOrder === SORT_ORDER.OLDEST ? 'Start Of Run' : 'End Of Run';
    }

    return querySortOrder === SORT_ORDER.OLDEST ? 'Start Time Window' : 'End Time Window';
  }, [exploreFlowLogs, querySortOrder, selectedRunId]);

  const getLogScrollerOptionValue = useCallback(
    (_option: any, index: number) => {
      if (!exploreFlowLogs) return undefined;
      return index === 0 ? SORT_ORDER.OLDEST : SORT_ORDER.NEWEST;
    },
    [exploreFlowLogs],
  );

  const loading = isFetchingLogs && !showInitialLoader;

  const logListHeader = useMemo(
    () => () => {
      const isActiveRefreshZone = viewSortOrder === SORT_ORDER.NEWEST;
      return <LogListHeader showReachedEndMessage={false} loading={isActiveRefreshZone} />;
    },
    [viewSortOrder],
  );

  const logListFooter = useMemo(
    () => () => {
      const showReachedEndMessage = !paging.hasMore && totalLogCount > 0;
      const isActiveRefreshZone = viewSortOrder === SORT_ORDER.OLDEST && !paging.hasMore;

      return (
        <LogListFooter
          loading={isActiveRefreshZone}
          showReachedEndMessage={showReachedEndMessage}
        />
      );
    },

    [paging.hasMore, totalLogCount, loading, viewSortOrder],
  );

  const renderLogItem = useCallback(
    (item: FlattenedLogItem, _index: number) => {
      if (item.type === 'skeleton') {
        return (
          <SkeletonLogLine
            key={item.id}
            lineNumberElementWidth={lineNumberElementWidth}
            lineNumber={item.lineNumber}
          />
        );
      }

      if (item.type === 'empty') {
        return (
          <div className="log-line info-line" key={item.id}>
            <span style={{ width: lineNumberElementWidth }} />
            <span className={s.greyscale400Text}>
              {meta?.total_count === 0
                ? selectedRunId && dateRange.from === toInteger(selectedRunId)
                  ? ERROR_MESSAGES.NO_LOGS_FOR_RUN
                  : ERROR_MESSAGES.NO_LOGS_FOR_TIME_RANGE
                : ERROR_MESSAGES.NO_LOGS_FOR_FILTER}
            </span>
          </div>
        );
      }

      return (
        <LogLine
          key={item.id}
          logObject={item?.log!}
          lineNumber={item?.lineNumber!}
          lineNumberElementWidth={lineNumberElementWidth}
          onClick={(event) => handleLogLineClick(event, item)}
          onKeyDown={(event) => handleLogLineKeyDown(event, item)}
        />
      );
    },
    [
      lineNumberElementWidth,
      meta,
      dateRange,
      selectedRunId,
      isFetchingLogs,
      handleLogLineClick,
      handleLogLineKeyDown,
    ],
  );

  const logScrollerOptions = useMemo(() => {
    const startLabel = exploreFlowLogs && !selectedRunId ? 'Start Time Window' : 'Start Of Run';
    const endLabel = exploreFlowLogs && !selectedRunId ? 'End Time Window' : 'End Of Run';

    return [
      {
        onClick: () => setQuerySortOrder(SORT_ORDER.OLDEST),
        optionContent: (
          <div className={s.navigationOption}>
            <span>{startLabel}</span>
          </div>
        ),
      },
      {
        onClick: () => setQuerySortOrder(SORT_ORDER.NEWEST),
        optionContent: (
          <div className={s.navigationOption}>
            <span>{endLabel}</span>
          </div>
        ),
      },
    ];
  }, [exploreFlowLogs, selectedRunId]);

  const navigationHandlers = useMemo(
    () => (popoverLogObject ? getNavigationHandlers(popoverLogObject) : {}),
    [getNavigationHandlers, popoverLogObject],
  );

  const copyAllProps = {
    logs: logsToCopy,
    tooltip: copyAllTooltip,
    visible: isFlowInsightsV2Enabled,
  };

  const downloadTooltip = isTimeWindowMode
    ? 'Download all logs loaded for the selected time window'
    : 'Download all logs loaded for this run';

  const downloadFileName = useMemo(
    () =>
      buildLogsFileName({
        flowNodeId,
        isTimeWindowMode,
        selectedRunId,
        dateRange,
        severities: filterBy.severities,
        viewSortOrder,
      }),
    [flowNodeId, isTimeWindowMode, selectedRunId, dateRange, filterBy.severities, viewSortOrder],
  );

  const downloadLogsProps = {
    logs: logsToCopy,
    tooltip: downloadTooltip,
    fileName: downloadFileName,
    visible: isFlowInsightsV2Enabled,
  };

  return (
    <div className="flow-run-logs">
      <FlowRunLogsHeader
        fromConnectorLabel={metaInfo?.displayName}
        onClose={onClose}
        flowId={flowNodeId}
        onRefresh={actions.onRefresh}
        copyAll={copyAllProps}
        downloadLogs={downloadLogsProps}
      />

      {!omitNavigationPanelControls && (
        <LogsNavigationPanel
          value={startDate}
          dateRange={dateRange}
          selectedLevel={selectedSeverityLabel}
          runsOptions={runsOptions}
          selectedRunOption={selectedRunOption}
          levelOptions={levelOptions}
          onStartDateChanged={onStartDateChanged}
          clearSelectedRunOption={clearSelectedRunOption}
          exploreFlowLogsMode={exploreFlowLogs}
          expandSingleDaySelection={isFlowInsightsV2Enabled}
          sortOrder={viewSortOrder}
          onSortOrderChange={handleSortOrderChange}
          omitRunSelector={omitRunSelector}
          omitSortOrderSelector={omitSortOrderSelector}
          createdAt={createdAt}
          logScrollerOptions={logScrollerOptions}
          selectedLogScrollerValue={exploreFlowLogs ? querySortOrder : undefined}
          logScrollerLabel={logScrollerLabel}
          getLogScrollerOptionValue={getLogScrollerOptionValue}
        />
      )}

      {hasError && (
        <ErrorBanner message={ERROR_MESSAGES.FETCH_FAILED} onRetry={actions.onRefresh} />
      )}

      <div className="main-content-container">
        {!hasError && (
          <ErrorBoundary onError={console.error} FallbackComponent={ErrorFallback}>
            <LogList<FlattenedLogItem>
              key={`${dateRange.from}-${dateRange.to}`}
              items={logsWithSkeletons}
              renderItem={renderLogItem}
              loading={loading}
              totalCount={logsWithSkeletons.length}
              onLoadMore={handleLoadMoreLogs}
              hasMore={paging.hasMore}
              showInitialLoader={showInitialLoader}
              components={{ Header: logListHeader, Footer: logListFooter }}
            />
          </ErrorBoundary>
        )}
      </div>

      {popoverAnchorEl && popoverLogObject?.log && (
        <LogDetailPopover
          open={Boolean(popoverAnchorEl)}
          anchorEl={popoverAnchorEl}
          onClose={handlePopoverClose}
          logObject={popoverLogObject.log}
          {...navigationHandlers}
        />
      )}
    </div>
  );
};

export default FlowRunLogs;
